import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { isBoolean, isNumber, isPlainObject, isString } from 'lodash';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { TBaseDataItem } from '../../../types';

const CustomInputButton: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const baseScheme = baseInputValidationScheme;
  const validationScheme = useMemo(
    () => ({
      ...baseScheme,
      properties: {
        ...baseScheme.properties,
        initialValue: {
          type: 'custom',
          validator: (v: any) => isString(v) || isBoolean(v) || isNumber(v) || isPlainObject(v),
        },
      },
    }),
    [baseScheme]
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const {
    name,
    children,
    required,
    validation,
    initialValue,
    formOnly,
    label,
    description,
    placeholder,
  } = tagProps;
  const formContextInput = name ? context.form?.inputs[name] : undefined;
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-input',
      label,
      description,
      placeholder,
      type: 'button',
      value: initialValue,
      errors: [],
      touched: false,
      required,
      validation,
      initialValue,
      formOnly,
    }),
    [formOnly, initialValue, name, required, validation]
  );

  useEffect(() => {
    if (context.form && !formContextInput) {
      context.form.handleSetInput(name, initialState);
    }
  }, [context.form, formContextInput, initialState, name]);

  const handleClick = useCallback(
    (value: TBaseDataItem) => {
      if (!context.form) return;

      context.form?.handleSetValue(name, value);
    },
    [context.form, name]
  );

  const handleShowHelp = useCallback(() => setShowHelp(showHelp), [showHelp]);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      handleClick,
      handleShowHelp,
      showHelp,
    }),
    [
      formContextInput?.errors,
      formContextInput?.touched,
      handleClick,
      handleShowHelp,
      initialState.type,
      initialValue,
      name,
      required,
      validation,
      showHelp,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return null;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputButtonContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputButton.displayName = 'CustomInputButton';
export default CustomInputButton;
